import type { SystemFlow } from './index';

// Node width: 200px. Spacing: 380px between starts = 180px gap between nodes.
const X = (n: number) => n * 380;
const Y_MAIN = 80;
const Y_BRANCH = 300;

export const itemLifecycleFlow: SystemFlow = {
  id: 'item-lifecycle',
  title: 'Item Lifecycle',
  description: 'How a work idea moves from raw submission through triage, item work, deduplication, ranking, and governance to become a published priority.',
  audience: 'Everyone',
  nodes: [
    {
      id: 'stakeholder',
      type: 'actor',
      label: 'Stakeholder',
      description: 'Team member, partner, or customer submitting a work idea',
      position: { x: X(0), y: Y_MAIN },
    },
    {
      id: 'intake',
      type: 'process',
      label: 'Intake Submission',
      description: 'Item captured with title. Enters the system as a raw, unstructured work object.',
      domain: 'intake',
      position: { x: X(1), y: Y_MAIN },
    },
    {
      id: 'triage',
      type: 'process',
      label: 'Triage Assessment',
      description: 'An Item Manager reviews the item. Decides: Pursue, Defer, Reject, or request more info.',
      domain: 'intake',
      position: { x: X(2), y: Y_MAIN },
    },
    {
      id: 'rejected',
      type: 'data',
      label: 'Deferred / Rejected',
      description: 'Item exits the active pipeline. Logged for record.',
      position: { x: X(2), y: Y_BRANCH },
    },
    {
      id: 'shaping',
      type: 'process',
      label: 'Item Work',
      description: 'Item assigned a category, altitude, and attribute values — the scoring inputs.',
      domain: 'shaping',
      position: { x: X(3), y: Y_MAIN },
    },
    {
      id: 'dedupe',
      type: 'process',
      label: 'Dedupe Review',
      description: 'Checked against existing items. Merged if duplicate; confirmed unique otherwise.',
      domain: 'shaping',
      position: { x: X(4), y: Y_MAIN },
    },
    {
      id: 'pool',
      type: 'data',
      label: 'Item Pool',
      description: 'Ready, unique items grouped by category × altitude into a comparability pool.',
      domain: 'cycle',
      position: { x: X(5), y: Y_MAIN },
    },
    {
      id: 'session',
      type: 'process',
      label: 'Ranking Session',
      description: 'Participants apply the selected tool (RICE, WSJF, etc.) to score and rank items.',
      domain: 'cycle',
      position: { x: X(6), y: Y_MAIN },
    },
    {
      id: 'results',
      type: 'data',
      label: 'Session Results',
      description: 'Ranked order produced. Captures scores, participant input, and method used.',
      domain: 'cycle',
      position: { x: X(7), y: Y_MAIN },
    },
    {
      id: 'catchball',
      type: 'process',
      label: 'Catchball Review',
      description: 'Governance review of draft priorities. Stakeholders can propose changes via the loop.',
      domain: 'governance',
      position: { x: X(8), y: Y_MAIN },
    },
    {
      id: 'published',
      type: 'data',
      label: 'Published Priority List',
      description: "Ratified, locked priority order. The org's authoritative ranked list.",
      domain: 'output',
      position: { x: X(9), y: Y_MAIN },
    },
  ],
  edges: [
    {
      id: 'e-stakeholder-intake',
      from: 'stakeholder',
      to: 'intake',
      sourceLabel: 'Idea / Request',
      targetLabel: 'Raw Item',
    },
    {
      id: 'e-intake-triage',
      from: 'intake',
      to: 'triage',
      sourceLabel: 'Submitted Item',
      targetLabel: 'Triage Candidate',
      logic: 'state: RAW',
    },
    {
      id: 'e-triage-shaping',
      from: 'triage',
      to: 'shaping',
      sourceLabel: 'Accepted Item',
      targetLabel: 'Item Work Target',
      condition: 'outcome = Pursue',
    },
    {
      id: 'e-triage-rejected',
      from: 'triage',
      to: 'rejected',
      sourceLabel: 'Closed Item',
      targetLabel: 'Archived Record',
      condition: 'outcome = Defer / Reject',
    },
    {
      id: 'e-shaping-dedupe',
      from: 'shaping',
      to: 'dedupe',
      sourceLabel: 'Attributed Item',
      targetLabel: 'Dedupe Candidate',
      logic: 'state: SHAPED',
    },
    {
      id: 'e-dedupe-pool',
      from: 'dedupe',
      to: 'pool',
      sourceLabel: 'Unique Item',
      targetLabel: 'Pool Member',
    },
    {
      id: 'e-pool-session',
      from: 'pool',
      to: 'session',
      sourceLabel: 'Ranked Candidates',
      targetLabel: 'Session Items',
    },
    {
      id: 'e-session-results',
      from: 'session',
      to: 'results',
      sourceLabel: 'Participant Scores',
      targetLabel: 'Ranked List',
    },
    {
      id: 'e-results-catchball',
      from: 'results',
      to: 'catchball',
      sourceLabel: 'Draft Priorities',
      targetLabel: 'Governance Proposal',
    },
    {
      id: 'e-catchball-published',
      from: 'catchball',
      to: 'published',
      sourceLabel: 'Ratified Decision',
      targetLabel: 'Published Priority',
      logic: 'state: PRIORITIZED',
    },
  ],
};
